/**
 * The scales an agent describes their production on, shared by the agent
 * profile editor and an organization's "Who we're looking for" requirements
 * (spec 2026-09-18). One list, two readers: an org's "$5M+" and an agent's
 * "$5M-$10M" are indexes into the same array, so matching is a comparison and
 * the two sides cannot drift apart.
 *
 * Indexes are stored, not labels. Append new values at the end; reordering or
 * inserting would silently change every saved answer on both sides.
 */

/** profile_data.production.transactionRange indexes into this. */
export const TRANSACTION_RANGES = ["0-5", "6-15", "16-30", "31-50", "50+"] as const;

/** profile_data.production.volumeRange indexes into this. */
export const VOLUME_RANGES = ["$0-$1M", "$1M-$3M", "$3M-$5M", "$5M-$10M", "$10M+"] as const;

/** profile_data.production.marketFocus holds these ids. */
export const MARKET_FOCUS_OPTIONS = [
  { id: "residential", label: "Residential" },
  { id: "luxury", label: "Luxury" },
  { id: "first-time", label: "First-time Buyers" },
  { id: "investors", label: "Investors" },
  { id: "commercial", label: "Commercial" },
  { id: "land", label: "Land" },
] as const;

/** profile_data.production.specialties holds these labels verbatim. */
export const SPECIALTY_OPTIONS = [
  "Waterfront", "New Construction", "Relocation", "Condos", "Historic Homes",
  "Farm & Ranch", "Multi-family", "Probate", "Short Sales", "REO/Foreclosure",
] as const;

/** The minimums an organization may ask for, in years. */
export const EXPERIENCE_MINIMUMS = [1, 3, 5, 10] as const;

export const marketFocusLabel = (id: string): string =>
  MARKET_FOCUS_OPTIONS.find((o) => o.id === id)?.label ?? id;

/** "$5M+" for index 3: the lower bound of that range, as a minimum. */
export const volumeMinimumLabel = (index: number): string => {
  const range = VOLUME_RANGES[index];
  if (!range) return "";
  return range.endsWith("+") ? range : `${range.split("-")[0]}+`;
};

/** "16+" for index 2. */
export const transactionMinimumLabel = (index: number): string => {
  const range = TRANSACTION_RANGES[index];
  if (!range) return "";
  return range.endsWith("+") ? range : `${range.split("-")[0]}+`;
};
